import Modal from 'components/Luxkit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import CheckboxInput from 'components/Luxkit/Checkbox/CheckboxInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { rem } from 'polished'
import styled from 'styled-components'
import Caption from 'components/Luxkit/Typography/Caption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { POSITIVE_FEEDBACK, NEGATIVE_FEEDBACK } from '../../constants/feedbackType'
import RHFTextArea from 'components/Common/Form/Input/RHFTextArea'
import Group from 'components/utils/Group'

const ErrorLabel = styled(Caption)`
  background: ${props => props.theme.palette.messaging.critical.lightBackground};
  border: 1px solid ${props => props.theme.palette.messaging.critical.normalForeground};
  padding: ${rem(8)} ${rem(8)};
  margin-top: ${rem(8)};
`

const StyledSubtitleBodyText = styled(BodyText)`
  margin-bottom: ${rem(12)};
`

const LESA_DIDNT_UNDERSTAND_QUESTION = 'LESA didn’t understand my question(s)'
const THE_ANSWERS_I_GOT_WERE_INACCURATE = 'The answers I got were inaccurate'
const REPLIES_FROM_LESA_TOOK_TOO_LONG = 'Replies from LESA took too long'
const OTHER = 'I had a different issue (please provide details below)'

export type Issues =
  | typeof LESA_DIDNT_UNDERSTAND_QUESTION
  | typeof THE_ANSWERS_I_GOT_WERE_INACCURATE
  | typeof REPLIES_FROM_LESA_TOOK_TOO_LONG
  | typeof OTHER;

type FeedbackArgs = {
  issues: Array<Issues>,
  message: string
}

interface FeedbackModalProps {
  sentiment?: typeof POSITIVE_FEEDBACK | typeof NEGATIVE_FEEDBACK
}

function FeedbackModal({ sentiment }: FeedbackModalProps) {
  const modalContext = useModalElementContext<undefined | any>()

  const methods = useForm<FeedbackArgs>({
    mode: 'onBlur',
    shouldFocusError: true,
    reValidateMode: 'onBlur',
    defaultValues: {
      issues: [],
      message: '',
    },
  })

  const { handleSubmit, register, watch, formState: { errors } } = methods

  const issues = watch('issues')

  const onSubmitFeedback = useCallback((feedback: FeedbackArgs) => {
    modalContext.resolve(feedback)
  }, [modalContext])

  const onFooterAction = useCallback(() => {
    modalContext.resolve(undefined)
  }, [modalContext])

  const requiredMessage =
    (issues.includes(OTHER) && 'Please describe the issue you encountered with LESA Support Assistant') ||
    (sentiment === POSITIVE_FEEDBACK && 'Please describe your experience with our support assistant so we can improve!') ||
    false

  const reportIssueFlow = !sentiment || sentiment === NEGATIVE_FEEDBACK

  return <Modal
    title={sentiment ? 'Provide additional feedback' : 'Report an issue'}
    primaryActionText="Submit feedback"
    onPrimaryActionClick={handleSubmit(onSubmitFeedback)}
    secondaryActionText={!sentiment ? 'Cancel' : undefined}
    onSecondaryActionClick={!sentiment ? onFooterAction : undefined}
    size="L"
  >
    <StyledSubtitleBodyText variant="large">
      {reportIssueFlow && 'Which aspects of LESA did you find challenging?'}
      {sentiment === POSITIVE_FEEDBACK && 'We appreciate your efforts and valuable feedback, which will greatly contribute to improving the overall user experience.'}
    </StyledSubtitleBodyText>
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitFeedback)}>
        <VerticalSpacer gap={16}>
          {reportIssueFlow && <Group direction="vertical" gap={32}>
            <div>
              <CheckboxInput {...register('issues', { required: !issues.length ? 'Please select at least one issue' : false })} required={!issues.length} value={LESA_DIDNT_UNDERSTAND_QUESTION} size="large">{LESA_DIDNT_UNDERSTAND_QUESTION}</CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? 'Please select at least one issue' : false })} required={!issues.length} value={THE_ANSWERS_I_GOT_WERE_INACCURATE} size="large">{THE_ANSWERS_I_GOT_WERE_INACCURATE}</CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? 'Please select at least one issue' : false })} required={!issues.length} value={REPLIES_FROM_LESA_TOOK_TOO_LONG} size="large">{REPLIES_FROM_LESA_TOOK_TOO_LONG}</CheckboxInput>
              <CheckboxInput {...register('issues', { required: !issues.length ? 'Please select at least one issue' : false })} required={!issues.length} value={OTHER} size="large">{OTHER}</CheckboxInput>
            </div>
            {errors.issues && <ErrorLabel variant="medium">Please choose at least one issue</ErrorLabel>}
          </Group>}
          <RHFTextArea
            {...register('message', {
              required: requiredMessage,
            })}
            required={!!requiredMessage}
            label={reportIssueFlow ? 'Do you have any suggestions or comments to improve your experience with LESA?' : 'What do you feel about this chat?'}
            placeholder="Enter your feedback"
            resize="vertical"
          />
        </VerticalSpacer>
      </form>
    </FormProvider>
  </Modal>
}

export default FeedbackModal
